import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AdminRole, AuthUser } from "@/types/auth";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: AuthUser) => void;
  clear: () => void;
  /** True if the signed-in admin holds one of `roles` (SUPER_ADMIN always passes). */
  hasRole: (...roles: AdminRole[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setUser: (user) => set({ user }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null }),
      hasRole: (...roles) => {
        const user = get().user;
        if (!user) return false;
        // SUPER_ADMIN implicitly has access to everything.
        if (user.role === "SUPER_ADMIN") return true;
        return roles.includes(user.role);
      },
    }),
    {
      name: "eyy-admin-auth",
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    },
  ),
);
